import * as z from 'zod/mini';

import { findInvariantViolations } from './invariants';
import type { Category, Kink, KinklistDoc, Rating, Role } from './types';
import { LIMITS } from './types';

const id = z.string().check(z.minLength(1), z.maxLength(64));
const name = z.string().check(z.minLength(1), z.maxLength(LIMITS.name));

export const ratingSchema = z.object({
  id,
  name,
  color: z.string().check(z.regex(/^#[0-9A-F]{6}$/)),
}) satisfies z.ZodMiniType<Rating>;

export const roleSchema = z.object({ id, name }) satisfies z.ZodMiniType<Role>;

export const kinkSchema = z.object({
  id,
  name,
  ratings: z.record(id, id),
  comment: z.optional(z.string().check(z.maxLength(LIMITS.comment))),
}) satisfies z.ZodMiniType<Kink>;

export const categorySchema = z.object({
  id,
  name,
  roles: z.array(roleSchema).check(z.minLength(LIMITS.minRoles), z.maxLength(LIMITS.maxRoles)),
  kinks: z.array(kinkSchema),
  collapsed: z.optional(z.boolean()),
}) satisfies z.ZodMiniType<Category>;

/** A complete, valid v3 document: shape plus every cross-reference invariant. */
export const docSchema = z
  .object({
    v: z.literal(3),
    title: z.string().check(z.maxLength(LIMITS.title)),
    ratings: z.array(ratingSchema).check(z.minLength(LIMITS.minRatings)),
    categories: z.array(categorySchema),
    updatedAt: z.iso.datetime(),
  })
  .check(
    z.superRefine((doc, ctx) => {
      for (const message of findInvariantViolations(doc)) {
        ctx.addIssue({ code: 'custom', message, input: doc });
      }
    }),
  ) satisfies z.ZodMiniType<KinklistDoc>;

export function parseDoc(input: unknown): KinklistDoc | null {
  const result = docSchema.safeParse(input);
  return result.success ? result.data : null;
}
